import type { GameState } from './types';
import { appendEvent } from './event-log';

/**
 * Mulberry32 PRNG — deterministic 32-bit hash function.
 * Returns a float in [0, 1) and the next state.
 * No external dependency — inline implementation.
 *
 * Reference: https://gist.github.com/tommyettinger/46a874533244883189143505d203312c
 *
 * @param state - Current 32-bit integer RNG state
 * @returns { value, nextState } where value ∈ [0, 1) and nextState is the advanced state
 */
export function mulberry32(state: number): { value: number; nextState: number } {
  const nextState = (state + 0x6d2b79f5) | 0;
  let t = nextState;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return { value, nextState };
}

/**
 * Roll the dice for a specific player.
 * Reads minDiceRoll and maxDiceRoll from game state settings.
 * Mutates game state: increments rngState, appends DiceRollEvent to eventLog.
 * Returns the dice result (integer in [minDiceRoll, maxDiceRoll]).
 *
 * Deterministic: same rngState + same settings = same result.
 *
 * @param state - Mutable GameState. state.rngState and state.eventLog are mutated.
 * @param playerId - Actor whose dice event should be emitted.
 * @returns Integer in [settings.minDiceRoll, settings.maxDiceRoll]
 */
export function rollDiceForPlayer(state: GameState, playerId: string): number {
  const { minDiceRoll, maxDiceRoll } = state.settings;
  const { value, nextState } = mulberry32(state.rngState);

  // Map [0, 1) to [min, max] inclusive
  const range = maxDiceRoll - minDiceRoll + 1;
  const result = Math.floor(value * range) + minDiceRoll;

  // Mutate state
  state.rngState = nextState;

  // Append DiceRollEvent via appendEvent (sets timestamp automatically, notifies subscribers)
  appendEvent(state, {
    type: 'dice_roll',
    playerId,
    result,
  });

  return result;
}

/**
 * Roll the dice for the MVP active player.
 *
 * Prefer `rollDiceForPlayer` when the acting player is already known.
 */
export function rollDice(state: GameState): number {
  const playerId = state.turnOrder[state.currentPlayerIndex];
  return rollDiceForPlayer(state, playerId);
}
